import { Link as RemixLink, useRouteLoaderData } from "@remix-run/react";
import { Button, Text, Link } from "@chakra-ui/react";

import { useOptionalUser, useUserSubscriptionValue } from "#app/hooks/index.js";
import {
  ActiveRequestHelpPopover,
  HelpPopover,
} from "#app/components/index.jsx";
import { ManageSubscriptionButton } from "#app/components/buttons/index.jsx";
import { plans } from "#app/config/stripe.js";

import { PricingCard } from "./PricingCard.jsx";
import { actionButtonProps } from "./shared.jsx";

export function TrialPricingCard({
  name = "Free",
  colorScheme = "gray",
  ...props
} = {}) {
  const actionButton = useUserSubscriptionValue({
    none: () => (
      <Button
        as={RemixLink}
        to="/new"
        colorScheme={colorScheme}
        {...actionButtonProps}
      >
        Get Started
      </Button>
    ),
    trial: () => (
      <Button isDisabled colorScheme={colorScheme} {...actionButtonProps}>
        Get Started
      </Button>
    ),
    active: () => (
      <ManageSubscriptionButton
        colorScheme={colorScheme}
        {...actionButtonProps}
      >
        Downgrade to {name}
      </ManageSubscriptionButton>
    ),
  });

  const actionButtonHelperText = useUserSubscriptionValue({
    none: () => (
      <>
        <Link as={RemixLink} to="/new">
          Create a request
        </Link>
        {" to sign up"}
      </>
    ),
    trial: () => "You're currently on this plan",
    active: () => "Cancel with Stripe to downgrade",
  });

  const { pricingCurrency } = useRouteLoaderData("root");
  const user = useOptionalUser();
  const hasTrialNotifications =
    typeof user?.subscription.trialNotificationsLeft === "number";

  return (
    <PricingCard
      colorScheme={colorScheme}
      name={name}
      price={0}
      pricingCurrency={pricingCurrency}
      duration=""
      description="Try us out for free! No credit card required."
      features={[
        <Text>
          {plans.trial.trialNotifications} notifications
          {hasTrialNotifications && (
            <Text as="span" color="subtle" fontSize="sm">
              &nbsp;({user.subscription.trialNotificationsLeft} left)
            </Text>
          )}
        </Text>,
        "Email only",
        <Text>
          {plans.trial.maxActiveRequests}{" "}
          <ActiveRequestHelpPopover buttonText="active request" />
        </Text>,
        "Checks every 1-5 minutes",
      ]}
      actionButton={actionButton}
      actionButtonHelperText={actionButtonHelperText}
      {...props}
    />
  );
}
